import type { Feature, FeatureCollection, Geometry } from 'geojson';
import type { LayerPreviewStyle, PreviewGeometry } from './layerAssets';

/**
 * Pure builders for the popover's live-map preview: one hardcoded rectangular
 * map area used for every instance, with a realistic hardcoded sample geometry per data
 * type, styled from the layer's tiles-config paint. No React / no I/O — unit-testable.
 */

/** GeoJSON source id the preview style layers read from. */
export const PREVIEW_SOURCE_ID = 'layer-preview-sample';

/**
 * The predetermined preview viewport — a few downtown San Francisco blocks (~2.4:1, the
 * popover frame's aspect). Every popover renders this same rectangle.
 */
export const PREVIEW_BOUNDS: [[number, number], [number, number]] = [
  [-122.4061, 37.7894],
  [-122.3939, 37.7934],
];

/** A utility main following the street grid with a couple of bends. */
const SAMPLE_LINE: number[][] = [
  [-122.4052, 37.7899],
  [-122.403, 37.7907],
  [-122.4007, 37.7905],
  [-122.3987, 37.7914],
  [-122.3968, 37.7912],
  [-122.395, 37.7921],
];

/** Structures scattered along a corridor (deliberately multiple points). */
const SAMPLE_POINTS: number[][] = [
  [-122.404, 37.7903],
  [-122.4015, 37.7912],
  [-122.3995, 37.7905],
  [-122.3975, 37.7919],
  [-122.3955, 37.791],
];

/** An irregular parcel-shaped polygon. */
const SAMPLE_POLYGON: number[][] = [
  [-122.403, 37.7903],
  [-122.399, 37.79],
  [-122.3972, 37.791],
  [-122.398, 37.7924],
  [-122.4015, 37.7926],
  [-122.4035, 37.7915],
  [-122.403, 37.7903],
];

/** Center of the circle sample (drawn as a screen-radius circle layer). */
const SAMPLE_CIRCLE_CENTER: number[] = [-122.4, 37.7914];

const feature = (geometry: Geometry): Feature => ({ type: 'Feature', properties: {}, geometry });

/** The hardcoded sample features the preview draws for a geometry type. */
export function previewFeatureCollection(geometry: PreviewGeometry): FeatureCollection {
  switch (geometry) {
    case 'line':
      return { type: 'FeatureCollection', features: [feature({ type: 'LineString', coordinates: SAMPLE_LINE })] };
    case 'point':
      return {
        type: 'FeatureCollection',
        features: SAMPLE_POINTS.map((coordinates) => feature({ type: 'Point', coordinates })),
      };
    case 'polygon':
      return {
        type: 'FeatureCollection',
        features: [feature({ type: 'Polygon', coordinates: [SAMPLE_POLYGON] })],
      };
    case 'circle':
      return {
        type: 'FeatureCollection',
        features: [feature({ type: 'Point', coordinates: SAMPLE_CIRCLE_CENTER })],
      };
  }
}

/** Minimal mapbox style-layer spec shape (kept local so this module stays dependency-free). */
export type PreviewLayerSpec = {
  id: string;
  type: 'line' | 'circle' | 'fill';
  source: string;
  layout?: Record<string, unknown>;
  paint: Record<string, unknown>;
};

/**
 * The mapbox style layers that render the sample in the layer's own colors and line
 * style (dash pattern preserved for dashed lines). Polygon/circle apply the layer's
 * verbatim fill + outline pair — a fully transparent fill draws nothing, exactly like
 * the map — falling back to the single `color` for whichever slot has no literal.
 */
export function previewLayerSpecs(style: LayerPreviewStyle): PreviewLayerSpec[] {
  const { geometry, color, fillColor, outlineColor, dashArray } = style;
  switch (geometry) {
    case 'line':
      return [
        {
          id: 'layer-preview-line',
          type: 'line',
          source: PREVIEW_SOURCE_ID,
          layout: { 'line-cap': dashArray ? 'butt' : 'round', 'line-join': 'round' },
          paint: { 'line-color': color, 'line-width': 2.5, ...(dashArray ? { 'line-dasharray': dashArray } : {}) },
        },
      ];
    case 'point':
      return [
        {
          id: 'layer-preview-points',
          type: 'circle',
          source: PREVIEW_SOURCE_ID,
          paint: {
            'circle-radius': 5,
            'circle-color': color,
            'circle-stroke-color': '#FFFFFF',
            'circle-stroke-width': 1.5,
          },
        },
      ];
    case 'polygon':
      return [
        {
          id: 'layer-preview-polygon-fill',
          type: 'fill',
          source: PREVIEW_SOURCE_ID,
          paint: { 'fill-color': fillColor ?? color, 'fill-opacity': 0.25 },
        },
        {
          id: 'layer-preview-polygon-outline',
          type: 'line',
          source: PREVIEW_SOURCE_ID,
          layout: { 'line-join': 'round' },
          paint: { 'line-color': outlineColor ?? color, 'line-width': 2 },
        },
      ];
    case 'circle':
      return [
        {
          id: 'layer-preview-circle',
          type: 'circle',
          source: PREVIEW_SOURCE_ID,
          paint: {
            'circle-radius': 28,
            'circle-color': fillColor ?? color,
            'circle-opacity': 0.25,
            'circle-stroke-color': outlineColor ?? color,
            'circle-stroke-width': 2,
          },
        },
      ];
  }
}
